import ArgumentWrapper from '@/components/arguments/ArgumentWrapper';
import Panel from '@/components/panel/Panel';
import { FiAlertCircle, FiRotateCw, TiArrowRepeat } from '@/components/react-icons/main';
import getValue from '@/lib/getValue.js';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import argumentDataSources from '@/schema/argumentDataSources';
import appActions from '@/screens/apps/redux/actions/appActions';
import stepActions from '@/screens/workflows/redux/actions/stepActions';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { useMemo } from 'react';
import { LuNetwork } from 'react-icons/lu';
import { useSelector } from 'react-redux';
import SliderClose from './SliderClose';
import SliderHeader from './SliderHeader';
import SliderTitle from './SliderTitle';

const multiplierOptions = [
  { value: 1, label: '1x', icon: <TiArrowRepeat /> },
  { value: 2, label: '2x', icon: <TiArrowRepeat /> },
  { value: 3, label: '3x', icon: <TiArrowRepeat /> }
];

const RetriesSlider = ({ onClose, dataSource, dataSourcePath, dataSavePath, disabled = false }) => {
  const step = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state));
  const stepApp = useSelector((state) => workflowScreenSelectors.activeWorkflowStepApp(state));
  const isHttpRequest = stepApp?.slug === 'httpRequest';

  const optionsRetries = dataSource && !isHttpRequest ? useSelector((state) => argumentSelectors[dataSource].activeOptions(state)?.retries) : null;
  const actions = dataSource === argumentDataSources.workflowStep ? stepActions : appActions;

  const getRetriesValue = (key, defaultValue = undefined) => {
    if (isHttpRequest) {
      return step?.arguments?.[key] ?? defaultValue;
    }
    if (dataSource) {
      return optionsRetries?.[key] ?? defaultValue;
    }
    return step?.options?.retries?.[key] ?? defaultValue;
  };

  const enableRetries = getRetriesValue('enableRetries', false);
  const retryCount = getRetriesValue('retryCount', 3);
  const retryDelay = getRetriesValue('retryDelay', 1000);
  const retryOnStatusCodes = getRetriesValue('retryOnStatusCodes');
  const retryMultiplier = getRetriesValue('retryMultiplier', 1);
  const retryOnAnyError = getRetriesValue('retryOnAnyError', false);

  const computedDataSavePath = useMemo(() => {
    if (dataSavePath) {
      return `${dataSavePath}.retries`;
    }
    if (dataSourcePath === 'options') {
      return 'options.retries';
    }
    return 'options.retries';
  }, [dataSavePath, dataSourcePath]);

  const handleChange = (key, value) => {
    if (isHttpRequest) {
      stepActions.set(`arguments.${key}`, value);
    } else {
      const parsedValue = key === 'retryCount' ? Math.min(parseInt(value), 10) : value;
      actions.set(`${computedDataSavePath}.${key}`, parsedValue);
    }
  };

  const handleDeleteRetries = () => {
    if (isHttpRequest) {
      stepActions.set(`arguments.enableRetries`, false);
      stepActions.set(`arguments.retryCount`, null);
      stepActions.set(`arguments.retryDelay`, null);
      stepActions.set(`arguments.retryMultiplier`, null);
      stepActions.set(`arguments.retryOnStatusCodes`, null);
      stepActions.set(`arguments.retryOnAnyError`, null);
    } else {
      actions.set(computedDataSavePath, null);
    }
    onClose();
  };

  const isConfigured = useMemo(() => {
    return enableRetries;
  }, [enableRetries]);

  return (
    <>
      <SliderHeader onClose={onClose} showDelete={isConfigured} onDelete={handleDeleteRetries} disabled={disabled} />
      <SliderTitle //
        icon={FiRotateCw}
        title="Configure Retries"
        description="Configure retry behavior for failed requests."
        helpIconContext="workflowStepRetriesContext"
      />
      <Panel.Section headerless onDelete={isConfigured ? handleDeleteRetries : null}>
        <ArgumentWrapper>
          <Panel.Toggle //
            disabled={disabled}
            label="Enable Retries?"
            icon={FiRotateCw}
            active={enableRetries}
            onChange={(value) => handleChange('enableRetries', value)}
          />
        </ArgumentWrapper>
        {enableRetries && (
          <>
            <Panel.Input //
              disabled={disabled}
              label="Retry Delay"
              description="The delay in ms between retries."
              value={retryDelay}
              onChange={(e) => handleChange('retryDelay', e.target.value)}
              placeholder="1000"
              type="number"
              min={0}
            />
            <Panel.Input //
              disabled={disabled}
              label="Retry Max Attempts"
              description="The maximum number of retries."
              value={retryCount}
              onChange={(e) => handleChange('retryCount', e.target.value)}
              placeholder="3"
              type="number"
              min={0}
              max={10}
            />
            <Panel.Select //
              disabled={disabled}
              label="Retry Multiplier"
              description="This value will be used to multiply the delay on each retry iteration."
              options={multiplierOptions}
              value={getValue(multiplierOptions, retryMultiplier)}
              onChange={({ value }) => handleChange('retryMultiplier', value)}
              showOptionIcon
              useSearch={false}
            />
            <Panel.LexicalInput //
              disabled={disabled}
              label="Retry On Status Codes"
              icon={LuNetwork}
              value={retryOnStatusCodes}
              onChange={(value) => handleChange('retryOnStatusCodes', value)}
              placeholder="429, 500, 502, 503"
              description="Comma-separated status codes to retry on (e.g., 429, 500, 502, 503). If empty, defaults to 429 and network errors."
            />
            <ArgumentWrapper>
              <Panel.Toggle //
                disabled={disabled}
                label="Retry On Any Error"
                icon={FiAlertCircle}
                active={retryOnAnyError}
                onChange={(value) => handleChange('retryOnAnyError', value)}
                description="If enabled, retries will occur on any error, regardless of status code or error type."
              />
            </ArgumentWrapper>
          </>
        )}
      </Panel.Section>
      <SliderClose onClose={onClose} />
    </>
  );
};

export default RetriesSlider;
